import * as React from "react";
import * as ProgressPrimitive from "@radix-ui/react-progress";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const progressVariants = cva(
  "relative h-2 w-full overflow-hidden rounded-full",
  {
    variants: {
      variant: {
        default: "bg-primary/20",
        secondary: "bg-secondary/20",
        destructive: "bg-destructive/20",
        warning: "bg-yellow-100 dark:bg-yellow-900/20",
        success: "bg-green-100 dark:bg-green-900/20",
        info: "bg-blue-100 dark:bg-blue-900/20",
        muted: "bg-muted",
        accent: "bg-accent/20",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const progressIndicatorVariants = cva("h-full w-full flex-1 transition-all", {
  variants: {
    variant: {
      default: "bg-primary",
      secondary: "bg-secondary",
      destructive: "bg-destructive",
      warning: "bg-yellow-500 dark:bg-yellow-600",
      success: "bg-green-500 dark:bg-green-600",
      info: "bg-blue-500 dark:bg-blue-600",
      muted: "bg-muted-foreground",
      accent: "bg-accent",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface ProgressProps
  extends React.ComponentProps<typeof ProgressPrimitive.Root>,
    VariantProps<typeof progressVariants> {
  indicatorVariant?: VariantProps<typeof progressIndicatorVariants>["variant"];
}

function Progress({
  className,
  value,
  variant = "default",
  indicatorVariant,
  ...props
}: ProgressProps) {
  const indicatorVariantToUse = indicatorVariant || variant;

  return (
    <ProgressPrimitive.Root
      data-slot="progress"
      className={cn(progressVariants({ variant }), className)}
      {...props}
    >
      <ProgressPrimitive.Indicator
        data-slot="progress-indicator"
        className={cn(
          progressIndicatorVariants({ variant: indicatorVariantToUse }),
        )}
        style={{ transform: `translateX(-${100 - (value || 0)}%)` }}
      />
    </ProgressPrimitive.Root>
  );
}

export { Progress, progressVariants, progressIndicatorVariants };
